import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Leaf } from "lucide-react";
import { T } from "../EditContext";
import { fetchProducts } from "../../lib/api";
import { formatINR } from "../../lib/constants";
import { useCart } from "../../context/CartContext";
import { ButtonRing, useBusyAction } from "../../components/LoadingRing";
import { LogoRing } from "./LogoRing";

// Light-theme CSS uses !important on the utility classes, so custom colours are set on the node.
const BadgeChip = ({ b, index }) => {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (b.color) el.style.setProperty("color", b.color, "important");
        if (b.border_color) el.style.setProperty("border-color", b.border_color, "important");
        if (b.bg_color) el.style.setProperty("background-color", b.bg_color, "important");
    }, [b.color, b.border_color, b.bg_color]);

    return (
        <div ref={ref} data-testid={`hero-badge-${index}`}
            className={`mono text-[9px] md:text-[10px] tracking-[0.16em] border px-3 py-2.5 text-center ${
                b.color ? "" : "text-zinc-400"} ${b.border_color ? "" : "border-white/10"}`}>
            {b.text}
        </div>
    );
};

const HeroMedia = ({ p }) => {
    const url = p.media_url;
    const isVideo = (p.media_type === "video") || /\.(mp4|webm)(\?|$)/i.test(url || "");
    // "clean" lets a cut-out PNG sit straight on the page; "boxed" keeps the glass card around it.
    const clean = (p.media_frame || "boxed card").startsWith("clean");
    const media = isVideo ? (
        <video src={url} autoPlay muted playsInline preload="auto" data-testid="hero-media-video"
            className={clean ? "w-full h-auto object-contain" : "w-full h-auto object-cover"} />
    ) : (
        <img src={url} alt={p.media_alt || ""} data-testid="hero-media-image"
            className={clean
                ? "w-full h-auto object-contain drop-shadow-[0_24px_40px_rgba(0,0,0,0.10)]"
                : "w-full h-auto object-cover"} />
    );
    const frame = (
        <div className={clean ? "" : "glass rounded-sm overflow-hidden border border-white/10"}
            data-testid={clean ? "hero-media-clean" : "hero-media-boxed"}>
            {media}
            {p.media_caption && (
                <div className={`mono text-[10px] tracking-[0.16em] text-zinc-400 ${
                    clean ? "mt-3 text-center" : "px-4 py-3"}`}>{p.media_caption}</div>
            )}
        </div>
    );
    if (!p.media_href) return <div data-testid="hero-media">{frame}</div>;
    const external = /^https?:\/\//i.test(p.media_href);
    return external ? (
        <a href={p.media_href} target="_blank" rel="noreferrer" data-testid="hero-media-link">{frame}</a>
    ) : (
        <Link to={p.media_href} data-testid="hero-media-link">{frame}</Link>
    );
};

const MOTION = {
    "slow zoom in": "heroZoomIn",
    "slow zoom out": "heroZoomOut",
    "slow drift": "heroDrift",
};

// Parallax: the artwork drifts up more slowly than the page, which reads as depth rather than movement.
const useScrollParallax = (enabled, strength = 0.1) => {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (!enabled || !ref.current) return undefined;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        let frame = 0;
        const paint = () => {
            const el = ref.current;
            if (!el) return;
            const top = el.parentElement.getBoundingClientRect().top;
            const shift = Math.max(Math.min(-top * strength, 70), -70);
            el.style.transform = `scale(1.05) translate3d(0, ${shift}px, 0)`;
        };
        const onScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(paint);
        };
        paint();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            cancelAnimationFrame(frame);
        };
    }, [enabled, strength]);
    return ref;
};

// Full-bleed media behind the copy. The artwork keeps its right-hand subject visible while a
// scrim clears the left side so the headline stays readable on every screen.
const HeroBackdrop = ({ p, mobileBand }) => {
    const url = p.media_url;
    const isVideo = (p.media_type === "video") || /\.(mp4|webm)(\?|$)/i.test(url || "");
    const choice = p.media_motion || "slow zoom in";
    const parallax = choice === "moves as you scroll";
    const layer = useScrollParallax(parallax);
    const name = MOTION[choice];
    const motion = name ? {
        animationName: name,
        animationDuration: `${Math.max(Number(p.media_motion_seconds) || 30, 6)}s`,
        animationTimingFunction: "ease-in-out",
        animationIterationCount: "infinite",
        animationDirection: "alternate",
    } : undefined;
    const scrim = p.media_scrim || "medium";
    const desktopFocus = p.media_focus || "68% center";
    const mobileFocus = p.media_focus_mobile || "58% top";
    const splitMobile = !mobileBand && !!p.media_url_mobile;

    return (
        <div className={`absolute inset-0 overflow-hidden ${mobileBand ? "hidden lg:block" : ""}`}
            data-testid="hero-backdrop">
            <div ref={layer} className={`absolute inset-0 ${parallax ? "hero-motion" : ""}`}
                data-testid="hero-parallax-layer">
                {isVideo ? (
                    <video src={url} autoPlay muted loop playsInline preload="auto" data-testid="hero-media-video"
                        style={{ objectPosition: desktopFocus, ...(motion || {}) }}
                        className="hero-motion absolute inset-0 w-full h-full object-cover" />
                ) : (
                    <>
                        {splitMobile && (
                            <img src={p.media_url_mobile} alt={p.media_alt || ""} data-testid="hero-media-image-mobile"
                                style={{ objectPosition: mobileFocus, ...(motion || {}) }}
                                className="hero-motion lg:hidden absolute inset-0 w-full h-full object-cover" />
                        )}
                        <img src={url} alt={p.media_alt || ""} data-testid="hero-media-image"
                            style={{ objectPosition: desktopFocus, ...(motion || {}) }}
                            className={`hero-motion absolute inset-0 w-full h-full object-cover ${
                                splitMobile ? "hidden lg:block" : ""}`} />
                    </>
                )}
            </div>
            <div className="absolute inset-0 hero-scrim" style={{ opacity: scrim === "soft" ? 0.7 : 1 }} />
            {scrim === "strong" && <div className="absolute inset-0 hero-scrim" style={{ opacity: 0.75 }} />}
        </div>
    );
};

const RING_FACTOR = {
    "matches heading height": 1,
    small: 0.6,
    medium: 0.8,
    large: 1.2,
    "extra large": 1.45,
};

const TITLE_SIZES = {
    small: "text-[2.4rem] sm:text-5xl md:text-6xl lg:text-[4.6rem] leading-[0.92]",
    medium: "text-[3.1rem] sm:text-6xl md:text-7xl lg:text-[6.4rem] leading-[0.88]",
    large: "text-[3.6rem] sm:text-7xl md:text-[5.4rem] lg:text-[7.4rem] leading-[0.86]",
    "extra large": "text-[4rem] sm:text-[4.8rem] md:text-[6.2rem] lg:text-[8.4rem] leading-[0.84]",
};

// The ring is sized and centred against the real headline box, so it always matches the
// height of the title (e.g. "Where…" through "…Science") whatever the screen width.
const useRingBox = (enabled, factor, nudgeX, nudgeY) => {
    const sectionRef = React.useRef(null);
    const titleRef = React.useRef(null);
    const [box, setBox] = React.useState(null);

    React.useEffect(() => {
        if (!enabled) return undefined;
        const measure = () => {
            const s = sectionRef.current;
            const tEl = titleRef.current;
            if (!s || !tEl) return;
            if (window.innerWidth < 1024) return setBox(null);
            const sr = s.getBoundingClientRect();
            const tr = tEl.getBoundingClientRect();
            const size = Math.max(Math.min(Math.round(tr.height * factor), Math.round(sr.width * 0.35)), 140);
            const gap = Math.min(56, sr.width * 0.035);
            const maxLeft = sr.width - size - 24;
            const left = Math.min(tr.right - sr.left + gap, Math.max(maxLeft, 0));
            const top = tr.top - sr.top + tr.height / 2 - size / 2;
            setBox({
                size,
                left: left + (Number(nudgeX) || 0) / 100 * sr.width,
                top: top + (Number(nudgeY) || 0) / 100 * sr.height,
            });
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (sectionRef.current) ro.observe(sectionRef.current);
        if (titleRef.current) ro.observe(titleRef.current);
        window.addEventListener("resize", measure);
        const raf = requestAnimationFrame(measure);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", measure);
            cancelAnimationFrame(raf);
        };
    }, [enabled, factor, nudgeX, nudgeY]);

    return { sectionRef, titleRef, box };
};

export const HeroSection = ({ props: p = {} }) => {
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
    const hero = products.find((x) => x.slug === (p.product_slug || "redrive-max"));
    const { addItem } = useCart();
    const [heroAdding, runHeroAdd] = useBusyAction();
    const badges = (p.badges || []).map((b) => (typeof b === "string" ? { text: b } : b || {}));
    const hasMedia = !!p.media_url && p.media_type !== "none";
    const backdrop = hasMedia && (p.media_layout || "").startsWith("behind");
    // With a dedicated mobile picture we show it whole, below the copy, instead of cropping it behind text.
    const mobileBand = backdrop && !!p.media_url_mobile;
    const ringOn = (p.logo_ring || "").startsWith("right");
    const ringFactor = RING_FACTOR[p.logo_ring_size] ?? 1;
    const ringSpin = Number(p.logo_ring_seconds) || 2.5;
    const ringOnMobile = ringOn && (p.logo_ring_mobile || "below the text") !== "hidden";
    const { sectionRef, titleRef, box: ringBox } = useRingBox(ringOn, ringFactor, p.logo_ring_nudge_x, p.logo_ring_nudge_y);
    const showCard = hero && p.show_product_card !== false && !backdrop;
    const titleSize = TITLE_SIZES[p.title_size] || (backdrop
        ? "text-[3.1rem] sm:text-6xl md:text-7xl lg:text-[6.4rem] leading-[0.88]"
        : "text-6xl md:text-7xl lg:text-[7rem] leading-[0.86]");

    return (
        <section ref={sectionRef} className={`relative overflow-hidden ${backdrop
            ? `pt-28 md:pt-32 lg:pb-16 lg:min-h-[74vh] lg:flex lg:items-center ${
                mobileBand ? "pb-0" : "pb-[38vh] sm:pb-[32vh]"}`
            : "pt-28 md:pt-32 pb-10 md:pb-20"}`} data-testid="hero-section">
            {backdrop && <HeroBackdrop p={p} mobileBand={mobileBand} />}
            {ringOn && ringBox && (
                <div className="absolute z-10 pointer-events-none hidden lg:block"
                    style={{ left: ringBox.left, top: ringBox.top }} data-testid="hero-logo-ring-slot">
                    <LogoRing glyph={p.logo_ring_glyph || ""} size={ringBox.size} spinSeconds={ringSpin} />
                </div>
            )}
            {!backdrop && (
                <>
                    <div className="pointer-events-none absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full"
                        style={{ background: "radial-gradient(circle, rgba(91,163,43,0.18) 0%, rgba(242,242,247,0) 65%)" }} />
                    <div className="pointer-events-none absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full"
                        style={{ background: "radial-gradient(circle, rgba(212,160,23,0.14) 0%, rgba(242,242,247,0) 65%)" }} />
                </>
            )}

            <div className={`relative z-10 mx-auto max-w-7xl px-6 md:px-12 grid lg:grid-cols-12 gap-8 lg:gap-2 items-center ${
                backdrop ? "w-full" : ""}`}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
                    className={`z-10 ${backdrop ? "lg:col-span-7" : "lg:col-span-6"}`}>
                    <div className="inline-flex items-center gap-2 mono text-[11px] tracking-[0.3em] text-gold mb-6">
                        <Leaf className="w-3 h-3" /> <T f="eyebrow" value={p.eyebrow} />
                    </div>
                    <h1 ref={titleRef} className={`font-display uppercase ${titleSize}`}
                        style={p.title_color ? { color: p.title_color } : undefined} data-testid="hero-title">
                        <T f="title_top_prefix" value={p.title_top_prefix} />{" "}
                        <span style={p.title_top_accent_color ? { color: p.title_top_accent_color } : undefined}>
                            <T f="title_top_accent" value={p.title_top_accent}
                                className={p.title_top_accent_color ? "" : "text-lime"} />
                        </span>.<br />
                        <T f="title_bottom_prefix" value={p.title_bottom_prefix} />{" "}
                        <span style={p.title_bottom_accent_color ? { color: p.title_bottom_accent_color } : undefined}>
                            <T f="title_bottom_accent" value={p.title_bottom_accent}
                                className={p.title_bottom_accent_color ? "" : "text-gold"} />
                        </span>.
                    </h1>
                    <p className="mt-6 text-zinc-400 text-lg max-w-xl leading-relaxed">
                        <T f="body" value={p.body} multiline />
                        <T f="tagline" value={p.tagline} as="span"
                            className="block mt-3 mono text-[11px] tracking-[0.28em] text-gold" />
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        {p.primary_label && (
                            <Link to={p.primary_href || "/shop"} className="btn-pill btn-primary" data-testid="hero-buy-now-button">
                                <T f="primary_label" value={p.primary_label} /> <ArrowRight className="w-4 h-4" />
                            </Link>
                        )}
                        {p.secondary_label && (
                            <Link to={p.secondary_href || "/science"} className="btn-pill btn-secondary" data-testid="hero-learn-more-button">
                                <T f="secondary_label" value={p.secondary_label} />
                            </Link>
                        )}
                    </div>

                    {badges.length > 0 && (
                        <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg" data-testid="hero-badges">
                            {badges.filter((b) => b.text).map((b, i) => (
                                <BadgeChip key={`${b.text}-${i}`} b={b} index={i} />
                            ))}
                        </div>
                    )}

                    {ringOnMobile && (
                        <div className="lg:hidden mt-10 flex justify-center" data-testid="hero-logo-ring-mobile">
                            <LogoRing glyph={p.logo_ring_glyph || ""} spinSeconds={ringSpin}
                                size={Math.min(Math.round(210 * ringFactor), 260)} />
                        </div>
                    )}
                </motion.div>

                <div className={`lg:col-span-6 relative space-y-5 ${backdrop ? "hidden lg:block" : ""}`}>
                    {hasMedia && !backdrop && <HeroMedia p={p} />}
                    {showCard && (
                        <div className="glass rounded-sm p-6 md:p-8 relative overflow-hidden">
                            <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full"
                                style={{ background: "radial-gradient(circle, rgba(91,163,43,0.22) 0%, rgba(242,242,247,0) 65%)" }} />
                            <div className="relative flex items-center justify-center -mt-2 mb-2">
                                {hero.images?.[0] && (
                                    <img src={hero.images[0]} alt={hero.name} data-testid="hero-product-image"
                                        className="h-[340px] md:h-[400px] w-auto object-contain drop-shadow-[0_30px_40px_rgba(0,0,0,0.18)]" />
                                )}
                            </div>
                            <div className="relative">
                                <div className="flex items-center justify-between flex-wrap gap-2">
                                    <div>
                                        <div className="eyebrow !text-[10px]">Best Seller</div>
                                        <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight mt-0.5">{hero.name}</h3>
                                        <div className="text-zinc-500 uppercase tracking-widest text-[10px] mt-0.5">
                                            {hero.flavor} · {hero.serving_count} Tablets
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-display text-3xl">{formatINR(hero.price)}</div>
                                        {hero.compare_at_price && <div className="text-xs text-zinc-500 line-through">{formatINR(hero.compare_at_price)}</div>}
                                    </div>
                                </div>
                                <div className="mt-5 flex items-center gap-3">
                                    <button onClick={() => runHeroAdd(() => addItem(hero, 1))} disabled={heroAdding}
                                        data-testid="hero-add-to-cart-button" className="btn-pill btn-primary flex-1">
                                        {heroAdding ? <ButtonRing label="ADDING…" /> : "Add to Cart"}
                                    </button>
                                    <Link to={`/product/${hero.slug}`} className="btn-pill btn-secondary">Details</Link>
                                </div>
                                <div className="mt-5 pt-4 border-t border-white/10 grid grid-cols-3 gap-2 mono text-[9px] tracking-[0.14em] text-zinc-400 text-center">
                                    <div>✓ No Sugar</div><div>✓ No Artificial Colours</div><div>✓ Gentle on Stomach</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {mobileBand && (
                <div className="lg:hidden mt-8" data-testid="hero-mobile-band">
                    <img src={p.media_url_mobile} alt={p.media_alt || ""} data-testid="hero-media-image-mobile"
                        className="w-full h-auto block" />
                </div>
            )}
        </section>
    );
};
